import React, { useRef, useState } from "react";
import { Platform, Pressable, View } from "react-native";
import { theme, Panel, Text, Heading } from "@clearabee/ui-library-native";
import { useFormContext } from "lib/hooks/useFormContext";
import {
  QuestionHelper,
  useQuestionContext,
} from "../../lib/providers/questionProvider";
import { useTranslation } from "react-i18next";
import { styles } from "./Question.styles";
import { useLayoutContext } from "components/Layout/useLayoutContext";
import { useEffect } from "react";

type QuestionChildrenProps<
  FormState extends Record<string, unknown>,
  PreviousValue = null,
> = {
  context: QuestionHelper<FormState>;
  previousValue: PreviousValue;
};

interface QuestionProps<
  FormState extends Record<string, unknown>,
  CurrentId extends keyof FormState & string,
  PreviousId extends keyof FormState & string,
> {
  id: CurrentId;
  previousId?: PreviousId;
  question: string;
  children:
    | React.ReactElement
    | ((
        props: QuestionChildrenProps<FormState, FormState[PreviousId]>,
      ) => React.ReactElement);
  formatAnswer?: (
    answer: NonNullable<FormState[CurrentId]>,
  ) => string | string[];
}

type OpenQuestionProps = Pick<
  QuestionProps<any, any, any>,
  "question" | "children" | "previousId"
> & { previousValue: unknown };

const OpenQuestion = ({
  children,
  question,
  previousValue,
  previousId,
}: OpenQuestionProps) => {
  const questionRef = useRef<View>(null);
  const context = useQuestionContext();
  const { scrollTo } = useLayoutContext();
  const [top, setTop] = useState<number>();

  useEffect(() => {
    typeof top !== "undefined" &&
      setTimeout(() => scrollTo(top, Platform.OS === "web"), 0);
  }, [top]);

  return (
    <View
      ref={questionRef}
      onLayout={(layout) => {
        setTop(layout.nativeEvent.layout.y);
      }}
    >
      <Heading
        level={4}
        color={Platform.OS === "web" ? "accent" : "brand"}
        styles={{
          alignSelf: "center",
          marginTop: previousId ? theme.spacing.small : theme.spacing.xlarge2,
        }}
      >
        {question}
      </Heading>
      {typeof children === "function"
        ? children({
            previousValue,
            context,
          })
        : children}
      <View style={{ marginBottom: theme.spacing.small }} />
    </View>
  );
};

export const Question =
  <FormState extends Record<string, unknown>>() =>
  <
    CurrentId extends keyof FormState & string,
    PreviousId extends keyof FormState & string,
  >({
    id,
    previousId,
    children,
    question,
    formatAnswer,
  }: QuestionProps<
    FormState,
    CurrentId,
    PreviousId
  >): React.ReactElement | null => {
    const { watch } =
      useFormContext<Record<string, null | undefined | unknown>>();
    const [translate] = useTranslation();

    const context = useQuestionContext<FormState>();
    const [value, previousValue] = watch(
      [id, previousId].filter(Boolean) as (keyof FormState & string)[],
    ) as [FormState[CurrentId], FormState[PreviousId]];

    if (context.is(id, "hidden")) return null;

    if (context.is(id, "closed") || context.is(id, "revisit")) {
      return (
        <Pressable
          style={{
            marginBottom: theme.spacing.small,
            opacity: context.is(id, "revisit") ? 0.5 : 1,
          }}
          onPress={() => {
            context.reOpen(id);
          }}
        >
          <Panel styles={styles.panel} shadow={Platform.OS === "web"}>
            <View style={{ flex: 1 }}>
              <Text color="brand">{question}</Text>
              <Text styles={{ fontFamily: "ProximaSemiBold" }}>
                {/* Typescript doesn't exclude null and undefined, hence the cast below */}
                {formatAnswer?.(value as NonNullable<FormState[CurrentId]>)}
                {/* Don't risk trying to print objects, can throw errors */}
                {!formatAnswer && (typeof value === "string" ? value : "")}
              </Text>
            </View>
            <Text color="brand" styles={styles.editButton}>
              {translate("common.edit")}
            </Text>
          </Panel>
        </Pressable>
      );
    }

    return (
      <OpenQuestion
        question={question}
        previousValue={previousValue}
        previousId={previousId}
      >
        {children}
      </OpenQuestion>
    );
  };
